import React, { forwardRef } from 'react';

import { Box, Divider, Flex } from '@chakra-ui/react';
import { useOKRAccessControl } from 'components/RabbitOKRSidePanel/AccessControl/useOKRAccessControl';
import { RealignModal } from 'components/RabbitOKRSidePanel/Components/RealignModal';
import { SectionHeader } from 'components/RabbitOKRSidePanel/Components/SectionHeaders';
import { useChangeTimelineRealignModalLogic } from 'components/RabbitOKRSidePanel/SecondaryPanel/RealignPanel/useChangeTimelineRealignModalLogic';
import { useViewPageName } from 'components/Views/Hooks/useViewPageName';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useSelector } from 'react-redux';
import { kRDrawerSelector } from 'selectors/kRDrawer';

import { Realign } from 'assets/assetsV1/iconComponents';

import { OKR_TYPES } from 'constants/ProjectConstants';
import { sendClickedRealignedEvent } from 'constants/mixpanelHelper';

import { Button } from 'uiToolkitV1/Button';

import OKRTopHierarchy from './OKRTopHierarchy';

const AlignmentSectionHeader = ({ handleRealignClick, canRealign }) => {
  const { moduleName } = useOrganisationSettings();
  return (
    <SectionHeader headerText={`Parent ${moduleName} Alignment`}>
      {canRealign ? (
        <Button StartIcon={Realign} variant="outlined" style={{ height: '32px' }} onClick={handleRealignClick}>
          Re-align
        </Button>
      ) : null}
    </SectionHeader>
  );
};

const Alignment = ({ isOKRPanelDisabled }) => {
  const { uuid, type, parentPost, timeBoundDetails, text, visibilityType, ifGoalChildExists, objectiveLevel } =
    useSelector(kRDrawerSelector);

  const { canEditOKR } = useOKRAccessControl();
  const page = useViewPageName();

  /* 
    Ideally this functionality can be extracted out and redux / context can be used.
    This requirement came a bit late and doing that will touch quite a few components, so doing it this way

    TODO: extract this hook and corresponding component to common component.
    use that component in one place and these components should only trigger opening and closing.
  */
  const {
    isChangeTimelineModalOpen,
    handleCloseModal,
    handleNotRealignChildrenOKRs,
    handleRealignChildrenOKRs,
    onSelectedTimeBoundPeriodChange,
  } = useChangeTimelineRealignModalLogic({
    details: {
      parentUuid: parentPost?.uuid,
      parentText: parentPost?.text,
      parentType: parentPost?.type,
      parentVisibilityType: parentPost?.visibilityType,
      okrTimePeriod: timeBoundDetails,
      okrUuid: uuid,
      okrType: type,
      okrText: text,
      okrVisibilityType: visibilityType,
      objectiveLevel,
      okrHasNoChildren: !ifGoalChildExists,
    },
  });

  const handleRealignClick = () => {
    sendClickedRealignedEvent({
      eventProperties: {
        source: type === OKR_TYPES.OBJECTIVE ? 'Edit Obj Panel' : 'Edit KR Panel',
        page,
        type: 'Realign',
        goalId: uuid,
      },
    });

    onSelectedTimeBoundPeriodChange(timeBoundDetails);
  };

  return (
    <>
      <Flex flexDirection="column" alignItems="stretch" gap="12px" flexWrap="nowrap" w="100%">
        <AlignmentSectionHeader
          handleRealignClick={handleRealignClick}
          canRealign={canEditOKR && !isOKRPanelDisabled}
        />
        <Divider sx={{ borderColor: '#CFD7DF' }} />

        <OKRTopHierarchy />
      </Flex>
      <RealignModal
        isOpen={isChangeTimelineModalOpen}
        onClose={handleCloseModal}
        handleRealignChildrenOKRs={handleRealignChildrenOKRs}
        handleNotRealignChildrenOKRs={handleNotRealignChildrenOKRs}
        okrType={type}
      />
    </>
  );
};

export default Alignment;

export const RefableAlignment = forwardRef((props, ref) => (
  <Box ref={ref} w="100%">
    <Alignment {...props} />
  </Box>
));
